import React, { useMemo } from 'react';
import type { TradeForm, SearchInventoryItem } from './types';
import { labelClassName, readOnlyInputClassName, isAmendLocked } from './types';
import { RegistryNotice } from './RegistryNotice';
import { registryNoticeIds } from './registry-notice-ids';
import type { Counterparty, CustodianItem } from '@/types';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui';

interface AccountDetailsSectionProps {
  form: TradeForm;
  isSubmitted: boolean;
  selectedInventoryItem: SearchInventoryItem | null;
  counterparties: Counterparty[];
  custodians: CustodianItem[];
  /** LR-625: mandatory only when no env fallback can supply the account base. */
  custodianRequired?: boolean;
  /** LR-625: registry genuinely empty — not loading, not failed. */
  custodiansEmpty?: boolean;
  /** LR-625: the registry read failed, so an empty list means nothing. */
  custodiansError?: boolean;
  /** Counterparty is *required*, so a failed read blocks the booking outright. */
  counterpartiesEmpty?: boolean;
  counterpartiesError?: boolean;
  amendMode?: boolean;
}

export const AccountDetailsSection: React.FC<AccountDetailsSectionProps> = ({
  form,
  isSubmitted,
  selectedInventoryItem,
  counterparties,
  custodians,
  custodianRequired = false,
  custodiansEmpty = false,
  custodiansError = false,
  counterpartiesEmpty = false,
  counterpartiesError = false,
  amendMode,
}) => {
  // Options come from the registries the form hook already loaded on open;
  // SearchableSelect filters them locally, so no extra per-keystroke requests.
  // The read is already active-only (LR-661); this restates the server's slice.
  const counterpartyOptions = useMemo<SearchableSelectOption[]>(
    () =>
      counterparties
        .filter((cp) => cp.isActive)
        .map((cp) => ({
          value: cp.name,
          // No "[GMRA]" tag (LR-634): the registry holds ONE agreement scalar, so
          // on a counterparty that has both it names whichever was filed first.
          label: cp.name,
          keywords: cp.clientId,
        })),
    [counterparties]
  );

  const custodianOptions = useMemo<SearchableSelectOption[]>(
    () =>
      custodians.map((c) => ({
        value: String(c.id),
        label: c.account_number ? `${c.name} (${c.account_number})` : c.name,
        keywords: c.short_code,
      })),
    [custodians]
  );

  // LR-495: "no security picked yet" disables inputs in CREATE only. In amend
  // (amendMode) editability is governed purely by the registry via isAmendLocked,
  // so the inputs no longer depend on a synthetic selectedInventoryItem.
  const inputsDisabled = !amendMode && !selectedInventoryItem;

  return (
    <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
      <div>
        <form.Field
          name="counterparty_name"
          validators={{
            onChange: ({ value }) => {
              if (!value || (typeof value === 'string' && !value.trim())) return 'Counterparty is required';
              return undefined;
            },
          }}
        >
          {(field) => (
            <>
              <label className={labelClassName}>
                Counterparty <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                value={field.state.value || ''}
                onChange={(selectedName) => {
                  field.handleChange(selectedName);
                  // Match the active-only filter used to build the options, so a
                  // duplicate name shared by an inactive record can't resolve to it.
                  const cp = counterparties.find((c) => c.isActive && c.name === selectedName);
                  if (cp) {
                    form.setFieldValue('counterparty_id', String(cp.id));
                  }
                }}
                options={counterpartyOptions}
                placeholder="Select Counterparty"
                searchPlaceholder="Search counterparty..."
                emptyMessage={counterpartiesEmpty ? 'No counterparties configured' : undefined}
                disabled={inputsDisabled || isAmendLocked(amendMode, 'counterparty_name')}
                buttonProps={{
                  'aria-label': 'Counterparty',
                  'aria-describedby': registryNoticeIds('counterparty_name', {
                    empty: counterpartiesEmpty,
                    error: counterpartiesError,
                  }),
                }}
              />
              <RegistryNotice
                registry="counterparties"
                idPrefix="counterparty_name"
                empty={counterpartiesEmpty}
                error={counterpartiesError}
              />
              {isSubmitted && field.state.meta.errors.length > 0 && (
                <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
              )}
            </>
          )}
        </form.Field>
      </div>
      <div>
        <form.Field
          name="custodian_id"
          validators={{
            onChange: ({ value }: { value: string | undefined }) =>
              custodianRequired && !value
                ? 'Custodian is required — no default account is configured'
                : undefined,
          }}
        >
          {(field) => (
            <>
              <label className={labelClassName}>
                Custodian {custodianRequired && <span className="text-red-500">*</span>}
              </label>
              <SearchableSelect
                value={field.state.value || ''}
                onChange={(v) => field.handleChange(v)}
                options={custodianOptions}
                placeholder="Select Custodian"
                searchPlaceholder="Search custodian..."
                emptyMessage={custodiansEmpty ? 'No custodians configured' : undefined}
                disabled={inputsDisabled || isAmendLocked(amendMode, 'custodian_id')}
                buttonProps={{
                  'aria-label': 'Custodian',
                  'aria-describedby': registryNoticeIds('custodian_id', {
                    empty: custodiansEmpty,
                    error: custodiansError,
                  }),
                }}
              />
              {/* SearchableSelect's own "failed" branch is server-mode only —
                  the union forbids isError in local filtering — and the dropdown
                  can only take a string, so both the failure and the way out are
                  stated beside the control (CashPoolField precedent). */}
              <RegistryNotice
                registry="custodians"
                idPrefix="custodian_id"
                empty={custodiansEmpty}
                error={custodiansError}
              />
              {/* Gated on custodianRequired, not just isSubmitted: the requirement
                  follows trade_type x settlement_venue (via the env base), and
                  switching to a venue the env covers drops it. The field's own
                  error state does NOT clear then — a change to another field does
                  not revalidate this one, and adding onChangeListenTo for those
                  two fields did not clear it either (measured: the message stayed
                  in field.state.meta.errors) — so the asterisk would go while the
                  red line stayed. Submitting re-runs the validator, so nothing is
                  hidden that would still block (kvi review). */}
              {isSubmitted && custodianRequired && field.state.meta.errors.length > 0 && (
                <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
              )}
            </>
          )}
        </form.Field>
      </div>
      <div>
        <form.Field name="custodian_account">
          {(field) => (
            <>
              <label className={labelClassName}>Custodian Account</label>
              <input
                type="text"
                value={field.state.value || ''}
                readOnly
                className={readOnlyInputClassName}
              />
            </>
          )}
        </form.Field>
      </div>
      <div>
        <form.Field
          name="counterparty_account"
          validators={{
            onChange: ({ value }) => {
              if (!value || !value.trim()) return 'Counterparty account is required';
              return undefined;
            },
          }}
        >
          {(field) => (
            <>
              <label className={labelClassName}>
                Counterparty Account <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={field.state.value || ''}
                readOnly
                className={readOnlyInputClassName}
              />
              {isSubmitted && field.state.meta.errors.length > 0 && (
                <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
              )}
            </>
          )}
        </form.Field>
      </div>
    </div>
  );
};
